import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography,
} from '@mui/material';
import {
  AccountBalanceOutlined as AccountIcon,
  AddRounded as AddIcon,
  CompareArrowsRounded as PairIcon,
  InfoOutlined as InfoIcon,
  PeopleAltOutlined as PeopleIcon,
} from '@mui/icons-material';
import { useBankAccounts } from './hooks/useBankAccounts';
import { useCustomers } from './hooks/useCustomers';
import LoanPairSetupCard from './components/dialogs/LoanPairSetupCard';

// หน้า "ตั้งค่าคู่ยืม-คืน" — แยกออกมาจากหน้า "ยืม-คืน" หลัก (LoanPairs.js) ตามที่ผู้ใช้ขอ
// เพราะส่วนกำหนด/แก้ไขบัญชีของคู่ยืม-คืนใช้ไม่บ่อย (ส่วนใหญ่ตั้งครั้งเดียวแล้วใช้แค่ดู
// ข้อมูลที่หน้า /accounting/loan-pairs) — เก็บ header banner + สร้าง/แก้ไขคู่ (รองรับ
// หลายคู่พร้อมกัน) ไว้ที่นี่ทั้งหมด ไม่มีการ์ดสรุปยอด/ประวัติ Payment เลย (อยู่ที่
// LoanPairs.js/LoanPairSummaryCard.js แทน)
export function LoanPairSetup({ title, subtitle }) {
  const { bankAccounts, loading, error, reload } = useBankAccounts();
  const { customerRows, findCustomer, customerName } = useCustomers();
  const [creatingNew, setCreatingNew] = useState(false);
  const pairs = bankAccounts.filter((account) => account.mode === 'BALANCE');
  const linkedCustomerCount = new Set(
    pairs.flatMap((pair) => [pair.customer_id, pair.customer_id_repay]).filter(Boolean),
  ).size;

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', pb: 5 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 3.5,
          color: '#fff',
          background: 'linear-gradient(125deg, #172554 0%, #1d4ed8 62%, #0ea5e9 100%)',
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 280,
            height: 280,
            borderRadius: '50%',
            right: -70,
            top: -150,
            bgcolor: 'rgba(255,255,255,.09)',
          },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <PairIcon fontSize="small" />
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.2, opacity: 0.85 }}>LOAN & REPAYMENT</Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.55rem', md: '2rem' } }}>
              {title || 'ตั้งค่าคู่ยืม-คืน'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, maxWidth: 650, opacity: 0.82 }}>
              {subtitle || 'เชื่อมโยงบัญชีฝั่งยืมและฝั่งคืน เพื่อติดตามยอดคงเหลือและประวัติรายการในที่เดียว'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setCreatingNew(true)}
            disabled={creatingNew}
            sx={{
              position: 'relative',
              zIndex: 1,
              alignSelf: { xs: 'stretch', md: 'center' },
              bgcolor: '#fff',
              color: '#1d4ed8',
              fontWeight: 750,
              px: 2.5,
              '&:hover': { bgcolor: '#eff6ff' },
            }}
          >
            เพิ่มคู่ยืม-คืน
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mt: 2.5 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: '#dbeafe' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#eff6ff', color: '#2563eb' }}><AccountIcon /></Box>
            <Box>
              <Typography variant="body2" color="text.secondary">คู่บัญชีที่ใช้งาน</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{pairs.length.toLocaleString('th-TH')} คู่</Typography>
            </Box>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: '#ccfbf1' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#f0fdfa', color: '#0f766e' }}><PeopleIcon /></Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Customer ที่เชื่อมโยง</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{linkedCustomerCount.toLocaleString('th-TH')} ราย</Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2.5 }}>{error}</Alert>}

      {creatingNew && (
        <Box sx={{ mt: 2.5 }}>
          <Alert icon={<InfoIcon />} severity="info" sx={{ mb: 1.5, borderRadius: 2.5 }}>
            เลือก Customer ทั้งสองฝั่ง ระบบจะดึงข้อมูลบัญชีธนาคารมาให้ตรวจสอบก่อนบันทึก
          </Alert>
          <LoanPairSetupCard
            pair={null}
            customerRows={customerRows}
            findCustomer={findCustomer}
            customerName={customerName}
            onSaved={() => { setCreatingNew(false); reload(); }}
            onCancelNew={() => setCreatingNew(false)}
          />
        </Box>
      )}

      {loading ? (
        <Paper variant="outlined" sx={{ mt: 2.5, minHeight: 240, display: 'grid', placeItems: 'center', borderRadius: 3 }}>
          <Stack spacing={1.5} alignItems="center"><CircularProgress size={32} /><Typography variant="body2" color="text.secondary">กำลังโหลดข้อมูลคู่บัญชี...</Typography></Stack>
        </Paper>
      ) : pairs.length === 0 && !creatingNew ? (
        <Paper variant="outlined" sx={{ mt: 2.5, p: { xs: 3, md: 5 }, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center', bgcolor: '#fafafa' }}>
          <Box sx={{ mx: 'auto', mb: 1.5, width: 56, height: 56, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#eff6ff', color: '#2563eb' }}><PairIcon /></Box>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>ยังไม่มีคู่ยืม-คืน</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>สร้างคู่แรกเพื่อเริ่มติดตามยอดยืม ยอดคืน และประวัติ Payment</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreatingNew(true)}>เพิ่มคู่ยืม-คืนคู่แรก</Button>
        </Paper>
      ) : (
        <Stack spacing={3} sx={{ mt: 2.5 }}>
          {pairs.map((pair, index) => (
            <Box key={pair.id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" label={`คู่ที่ ${index + 1}`} color="primary" variant="outlined" />
                <Typography variant="caption" color="text.secondary">{customerName(pair.customer_id)} ↔ {customerName(pair.customer_id_repay)}</Typography>
              </Stack>
              <LoanPairSetupCard pair={pair} customerRows={customerRows} findCustomer={findCustomer} customerName={customerName} onSaved={reload} />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default LoanPairSetup;
